export interface HelpAbschnitt {
  titel: string;
  punkte: string[];
}

export interface HelpContent {
  title: string;
  intro: string;
  points: string[];
  /**
   * Gegliederte Erklaerung fuer Seiten mit mehreren Formularen.
   *
   * Eine flache Liste aus zwanzig Punkten liest niemand — wer eine Frage zu
   * einem bestimmten Feld hat, sucht den Abschnitt und nicht den Punkt.
   * Seiten ohne `sections` zeigen weiter nur `points`.
   */
  sections?: HelpAbschnitt[];
}

export const HELP_CONTENT: Record<string, HelpContent> = {
  '/': {
    title: 'Dashboard',
    intro: 'Schneller Überblick über den Systemzustand.',
    points: [
      'Zeigt die Gesamtzahl laufender Instanzen und aktiver Server auf einen Blick.',
      'Die Tabelle "Recent Deployment Jobs" listet die letzten Hintergrundjobs (Create/Update/Start/Stop/Deploy) — hilfreich, um schnell zu sehen, ob gerade etwas fehlschlägt.',
      'Für Details zu einem Job: unter "Deployments" nachschauen, dort gibt es die volle Historie inkl. Logs.',
    ],
  },
  '/products': {
    title: 'Produkte',
    intro: 'Produktkatalog — was Kunden bestellen können und wie es technisch ausgerollt wird.',
    points: [
      'Jedes Produkt kann mehrere Pläne (Preisstufen) und Compose-Templates (Docker-Compose-Vorlagen) haben.',
      'Compose-Templates enthalten Platzhalter wie {{DOMAIN}}, {{STACK_NAME}} oder Release-Image-Tags — die werden beim Deployment automatisch mit Config, Secrets und dem gewählten Release befüllt.',
      'Config-Felder definieren, was Kunden/Operations pro Instanz einstellen können (z.B. Domain, Plan-Optionen).',
    ],
  },
  '/servers': {
    title: 'Server',
    intro: 'Die Docker-Hosts, auf denen Kundeninstanzen laufen.',
    points: [
      'Server können manuell angelegt werden oder automatisch über "Automatisch bereitstellen" bei Hetzner/Hostinger — dabei wird der Server wirklich bei dem Provider erstellt/gekauft.',
      'Ohne hinterlegten SSH-Key auf einem Server kann keine Instanz dorthin deployt werden — Key hier hinterlegen (wird verschlüsselt gespeichert), dann mit "SSH testen" prüfen.',
      'Die Slot-Anzeige zeigt Kapazitätsauslastung (aktive vs. maximale Instanzen pro Server).',
      '"Decommission" löscht bei Hetzner den Server auch wirklich beim Provider. Bei Hostinger geht das nicht automatisch (keine Cancel-API) — dort manuell im hPanel kündigen.',
    ],
  },
  '/instances': {
    title: 'Instanzen',
    intro: 'Die laufenden Kundeninstallationen (ein Docker-Compose-Stack pro Instanz).',
    points: [
      'Start/Stop/Restart/Deploy lösen echte SSH-Befehle auf dem zugewiesenen Server aus (docker compose up/down/start/stop).',
      'Im Tab "Secrets" werden verschlüsselte Werte verwaltet, die beim Deployment als Umgebungsvariablen in den Compose-Stack injiziert werden (z.B. DB-Passwörter, API-Keys).',
      'Im Tab "Jobs" siehst du die Deployment-Historie dieser Instanz inkl. SSH-Log-Ausgabe — hilfreich beim Debuggen fehlgeschlagener Deployments.',
    ],
  },
  '/orders': {
    title: 'Bestellungen',
    intro: 'Bestellungen — aus WooCommerce importiert oder manuell angelegt.',
    points: [
      'Durchlaufen einen Status-Workflow: DRAFT → PENDING_PAYMENT → PAID → PROVISIONING → ACTIVE.',
      '"Provisionieren" stößt die automatische Instanz-Erstellung an (queued einen Deployment-Job).',
      'Eine Bestellung ohne verknüpfte Instanz bedeutet meist, dass die Provisionierung noch aussteht oder fehlgeschlagen ist — dort in den Deployments nachsehen.',
    ],
  },
  '/customers': {
    title: 'Kunden',
    intro:
      'Kundenstammdaten samt Bestellungen, Instanzen und den Zugängen zum Kundenbereich. Auf eine Zeile klicken, um alles zu einem Kunden aufzuklappen.',
    points: [
      'Ein Kunde ist die Firma; darunter hängen beliebig viele Zugänge (Kontoinhaber, Buchhaltung, Technischer Kontakt, Nur Lesen). Diese Zugänge gelten ausschließlich für den Kundenbereich — sie haben keinerlei Zugriff auf das Provisioning Center.',
      'Eingeladene Zugänge bekommen kein Passwort, sondern einen Link zum Selbstsetzen — genau wie Konten, die nach einem Kauf automatisch entstehen.',
      'Einen Kunden zu sperren blockiert sofort alle seine Zugänge zum Kundenbereich und entwertet offene Einladungs-Links. Laufende Instanzen bleiben bewusst unberührt: Produktivsysteme abzuschalten ist eine eigene, ausdrückliche Entscheidung.',
      'Der letzte aktive Kontoinhaber eines Kunden lässt sich weder herabstufen noch deaktivieren — sonst käme der Kunde nicht mehr an sein eigenes Konto.',
      'Kunden, die über das Bestell-Widget oder den Shop kaufen, werden automatisch angelegt. Manuell anlegen ist vor allem für Verträge außerhalb des Shops nötig.',
    ],
  },
  '/deployments': {
    title: 'Deployments',
    intro: 'Vollständige Historie aller Hintergrundjobs (BullMQ-Queue).',
    points: [
      'Umfasst Instance-Jobs (Create/Update/Start/Stop/Redeploy/Delete) und Server-Jobs (Create/Prepare Server).',
      'Jeder Job hat ein Log-Feld mit dem kompletten SSH-Transkript bzw. den Provider-API-Aufrufen — der erste Ort zum Debuggen bei Fehlern.',
      'Fehlgeschlagene Jobs lassen sich hier erneut anstoßen (Retry).',
    ],
  },
  '/releases': {
    title: 'Releases',
    intro: 'Versionsverwaltung pro Produkt — welche Image-Tags aktuell ausgerollt werden.',
    points: [
      'Ein Release definiert Backend-/Frontend-/Worker-Image + Tag, die beim Instanz-Update ins Compose-Template eingesetzt werden.',
      'Workflow: DRAFT → freigeben (approve) → Rollout auf einen Release-Channel (z.B. stable/beta).',
      'Instanzen abonnieren einen Release-Channel und bekommen darüber ihre Updates.',
    ],
  },
  '/audit': {
    title: 'Audit-Log',
    intro: 'Wer hat wann was geändert — für Nachvollziehbarkeit.',
    points: [
      'Protokolliert sicherheitsrelevante und administrative Aktionen mit Vorher-/Nachher-Zustand, wo verfügbar.',
      'Nützlich, um z.B. nachzuvollziehen, wer eine Instanz gestoppt oder einen Secret geändert hat.',
    ],
  },
  '/integrations': {
    title: 'Integrationen',
    intro: 'API-Tokens für Cloud-Provider, die die automatische Bereitstellung nutzt.',
    points: [
      'Werte werden verschlüsselt in der DB gespeichert und wirken sofort — kein Neustart nötig.',
      'Ohne gültigen Token schlagen die Lookups im "Automatisch bereitstellen"-Dialog (Servertypen, Preise, Templates) und die eigentliche Bestellung fehl.',
      'Hostinger hat keinen Sandbox-Modus — ein hinterlegter Token ermöglicht echte Bestellungen gegen ein echtes Zahlungsmittel.',
    ],
  },
  '/api-keys': {
    title: 'API-Keys',
    intro: 'Zugang für CLI, MCP-Server und andere Agenten/Skripte — ohne Nutzer-Login.',
    points: [
      'Jeder Key hat eine Rolle (wie bei einem Nutzer-Account) — die bestimmt, welche Endpunkte er nutzen darf.',
      'Der Klartext-Key wird nur direkt nach dem Erstellen einmal angezeigt und danach nirgends gespeichert — bei Verlust muss ein neuer Key erstellt werden.',
      'Widerrufene Keys funktionieren sofort nicht mehr, unabhängig von Neustarts.',
    ],
  },
  '/embed-keys': {
    title: 'Embed-Keys',
    intro:
      'Ein Key je Marketing-Website. Damit zeigt die Seite Tarifkarten an und startet Registrierung und Checkout — ganz ohne eigenen Shop.',
    points: [
      'Der Key ist bewusst kein Geheimnis: er steht im Quelltext jeder Seite, die das Widget einbindet. Geschützt wird er über die Domain-Freigabe — von einer nicht freigegebenen Domain antwortet die API mit 403.',
      'Jede Domain zählt exakt: www.juni.com und juni.com sind zwei Einträge, http und https ebenfalls.',
      'Kunden, die über das Widget kaufen, bekommen ihr Konto erst nach erfolgreicher Zahlung. Abgebrochene Bestellungen hinterlassen nur eine Anfrage, die nach 24 Stunden verfällt — keine Karteileichen.',
      'Unter „Anfragen" sehen Sie je Website, wie viele Interessenten den Prozess gestartet und wie viele ihn abgeschlossen haben.',
      'Ein widerrufener Key wirkt sofort: das Widget auf der betreffenden Seite hört auf zu funktionieren, andere Seiten bleiben unberührt.',
      'Über „Einbindung" bekommen Sie den fertigen Code-Schnipsel für HTML, WordPress, TYPO3 und Magento.',
    ],
  },
  '/git-registry': {
    title: 'Git & Registry',
    intro:
      'Wer Zugang zum Quelltext hat, und was an Abzuegen liegenbleibt. Beides haengt an derselben Maschine.',
    points: [],
    sections: [
      {
        titel: 'Git-Konten',
        punkte: [
          'Die Liste zeigt jeden PC-Nutzer mit seinem Git-Konto daneben. Der Sinn der Seite steht in der Spalte „Abweichung": ein Kollege ist im PC gesperrt, sein Git-Konto aber noch offen — oder umgekehrt.',
          '„Alles geradeziehen" legt fehlende Konten an und gleicht Sperren in beide Richtungen ab. Es loescht nie etwas.',
          'Gesperrt statt geloescht: ein geloeschtes Konto nimmt seine Beitraege nicht mit, aber die Zuordnung der Commits zu einem Menschen wird unleserlich — und die braucht man Jahre spaeter noch.',
          'Ein neu angelegtes Konto bekommt ein Zufallskennwort, das niemand erfaehrt. Die Anmeldung laeuft ueber „Passwort vergessen" — ein hinterlegtes Anfangskennwort waere ein Geheimnis, das keiner braucht und das trotzdem irgendwann ausläuft.',
          'Repositories, Pull Requests und Pipelines bleiben in Forgejo selbst. Der Knopf oben rechts fuehrt hin.',
        ],
      },
      {
        titel: 'Registry — Aufbewahrung',
        punkte: [
          'Eine Docker-Registry loescht von selbst gar nichts. Jeder Build legt neue Daten ab, der alte Abzug bleibt liegen. So sind auf coruscant 62 GB entstanden.',
          '„Alles aelter als X weg" waere die naheliegende Regel und die gefaehrlichste: sie trifft irgendwann genau den einen alten Abzug, auf den man zurueck will. Stattdessen wird nach hinten hin ausgeduennt — bis 14 Tage jeder Abzug, dann einer je Tag, dann einer je Woche.',
          'Geschuetzt ist, was in einem Release steht, was direkt an einer Instanz haengt, und „latest" — unabhaengig vom Alter. Diese Abzuege werden auf Kundenservern gezogen; verschwindet einer, scheitert der naechste Pull dort, Stunden spaeter und ohne erkennbaren Zusammenhang.',
          'Beide Quellen zaehlen, nicht nur Releases: die meisten Instanzen hier haben gar kein Release hinterlegt und tragen ihren Bildtag selbst. Wer nur Releases schuetzt, raeumt genau deren Abzuege weg.',
          'Auch Entwuerfe und zurueckgezogene Releases schuetzen. Ein zurueckgezogenes ist der Stand, auf den man zurueckgeht, wenn das neue sich als Fehler erweist.',
          'Ein Abzug, dessen Datum sich nicht ermitteln laesst, wird behalten. Auf eine fehlende Antwort hin zu loeschen waere die falsche Richtung.',
        ],
      },
      {
        titel: 'Registry — die zwei Schritte',
        punkte: [
          'Loeschen nimmt nur die Verweise weg. Es gibt keinen Platz frei — wer danach auf die Plattenbelegung schaut, haelt das Aufraeumen fuer wirkungslos.',
          'Platz entsteht erst durch die Muellabfuhr. Die laeuft auf dem Host und braucht deshalb die Angabe, wo die Registry steht.',
          'Dafuer wird die Registry etwa eine Minute angehalten: die Muellabfuhr geht davon aus, dass sich nichts aendert. Wird parallel gepusht, kann sie Daten wegraeumen, die das frische Manifest schon braucht — das Ergebnis ist ein Abzug, der in der Liste steht und beim Ziehen abbricht.',
          'Bricht der Lauf unterwegs ab, startet die Registry trotzdem wieder. Sonst bliebe sie gestoppt liegen, und ab da schluege jeder Build fehl, ohne dass jemand es mit dem Aufraeumen in Verbindung braechte.',
          'Der Probelauf zeigt, was die Muellabfuhr taete, ohne die Registry anzuhalten.',
        ],
      },
      {
        titel: 'Wenn nichts geht',
        punkte: [
          'Steht bei Git „nicht eingerichtet": unter Einstellungen in der Gruppe „Forgejo" fehlen Adresse oder Administrator-Token. Bis dahin aendert der PC an Git-Konten nichts — auch nicht versehentlich.',
          'Meldet das Loeschen HTTP 405, erlaubt die Registry kein Loeschen. Dann fehlt ihr REGISTRY_STORAGE_DELETE_ENABLED.',
          'Der Zugang zum Proxy-Panel eines Servers wird nicht hier gesetzt, sondern in der Serververwaltung im Menue des jeweiligen Servers.',
        ],
      },
    ],
  },
  '/bereitschaft': {
    title: 'Bereitschaft und Warnungen',
    intro:
      'Wer bei einem Ausfall benachrichtigt wird, in welcher Reihenfolge — und was passiert, wenn niemand reagiert.',
    points: [],
    sections: [
      {
        titel: 'Wie das Ganze zusammenhängt',
        punkte: [
          'Die Überwachung meldet einen Alarm an den PC. Passt ein Bereitschaftsplan dazu, startet eine Kette: sie benachrichtigt die erste Person, wartet, nimmt die nächste, und steigt nach einer festgelegten Zeit eine Stufe höher.',
          'Die Kette endet, sobald jemand „Ich übernehme" drückt — oder wenn der Alarm sich erledigt hat.',
          'Ohne passenden Plan bleibt es beim alten Verhalten: die Meldung geht an den Sammelkanal, niemand wird gezielt angesprochen. Ein Alarm geht also nie verloren, nur weil ein Plan fehlt.',
        ],
      },
      {
        titel: 'Wie sich die Last verteilt',
        punkte: [
          'Die Uebersicht oben zeigt, wie oft jemand in den letzten Wochen herangezogen wurde. Bereitschaft verteilt sich nicht von selbst gleichmaessig: wer schnell antwortet, steht in mehr Plaenen, und wer in mehr Plaenen steht, wird oefter geweckt.',
          'Gewichtet: abends und nachts zaehlt doppelt, Wochenenden ebenso, Feiertage dreifach, und eine Uebernahme mehr als eine blosse Benachrichtigung. Ein Anruf am Dienstagvormittag und einer am Sonntag um drei sind nicht dasselbe.',
          'Gelb ab dem 1,35-fachen des Mittels, rot ab dem 1,8-fachen. Bei weniger als sechs Ereignissen im Zeitraum bleibt die Ampel grau — bei drei Vorfaellen waere „doppelt so viel wie der Durchschnitt" eine Aussage ueber den Zufall.',
          'Gelb und Rot sperren nichts. Beim Eintragen einer Person steht der Hinweis neben ihrem Namen, mehr nicht: der PC ersetzt kein Gespraech mit dem Kollegen, er sorgt nur dafuer, dass man die Lage kennt.',
          'Die Spalte „Plaene" ist die Ursache, nicht die Wirkung — dort faengt man an, wenn etwas schieflaeuft.',
          'Gezaehlt wird, wie oft jemand dran war, nicht wie er sich geschlagen hat. Keine Reaktionszeiten, keine Erfolgsquoten, keine Rangfolge. Das ist Absicht: die Auswertung soll Last verteilen helfen, nicht Menschen vergleichen.',
          'Das Protokoll darunter zeigt dasselbe chronologisch — fuer den Fall, dass zwei Kollegen unterschiedlich erinnern, wer an Weihnachten eingesprungen ist.',
        ],
      },
      {
        titel: 'Laufende Eskalationen',
        punkte: [
          '„Ich übernehme" stoppt die Kette und hält mit Uhrzeit fest, wer sich kümmert. Das darf jeder Angemeldete, auch wer nicht im Plan steht: wer gerade Zeit hat, soll die Kette stoppen können, statt zuzusehen, wie sie den nächsten Kollegen anspricht.',
          'Ein übernommener Vorfall bleibt in der Liste stehen. Er ist ja nicht gelöst, sondern nur betreut — sonst wäre die Liste am Morgen leer, obwohl jemand die halbe Nacht daran saß.',
          'Das Vorfallsprotokoll sammelt beides: was das System getan hat (benachrichtigt, eskaliert) und was Menschen eingetragen haben. Die wertvollste Zeile ist meist die, in der jemand schreibt, was NICHT geholfen hat.',
          '„Ich komme nicht weiter" gibt die Übernahme zurück und setzt die Kette bei der nächsthöheren Stufe fort. Ohne diesen Weg hätte ein überforderter Kollege nur die Wahl zwischen Weitermachen und Nichtstun — und die Kette bliebe stumm, weil sie ja jemand übernommen hat.',
        ],
      },
      {
        titel: 'Alarme',
        punkte: [
          'Kommen von der Überwachung und stehen hier unabhängig davon, ob eine Eskalation dazu läuft.',
          'Stummschalten geht nur befristet — eine Stummschaltung ohne Ende wäre eine abgeschaltete Überwachung, die niemand mehr einschaltet. Gedacht für angekündigte Wartung.',
          '„Probenachricht" schickt eine Testmeldung über alle eingerichteten Kanäle. Ein Kanal, der erst im Ernstfall benutzt wird, ist keiner.',
        ],
      },
      {
        titel: 'Plan: Geltungsbereich',
        punkte: [
          'Schweregrade: für welche Alarme der Plan gilt. „critical" allein ist der übliche Fall — bei „warning" wird auch für Dinge angesprochen, die Zeit hätten.',
          '„Nur für Server" oder „Nur für Kunde" grenzt den Plan ein. Leer heißt: gilt überall. Passt kein gezielter Plan, greift der als Vorgabe markierte.',
          '„Vorgabe für alles ohne eigenen Plan": genau ein Plan sollte das sein. Ohne ihn verschwinden Alarme, auf die keine Regel zutrifft.',
          '„Erinnerung alle … Minuten": in diesem Takt wird nachgehakt, solange niemand übernommen hat. Das ist unabhängig vom Sprung zur nächsten Stufe — der hat seine eigene Frist.',
        ],
      },
      {
        titel: 'Plan: Stufen und Personen',
        punkte: [
          'Eine Stufe ist eine Eskalationsebene — typisch: Verantwortliche, Vorgesetzte, Geschäftsführung.',
          '„ab Minute" zählt ab Alarmbeginn, NICHT ab Ende der Vorstufe. Steht bei Stufe 2 eine 180, ist der Vorgesetzte drei Stunden nach Alarmbeginn dran — unabhängig davon, wie viele Leute in Stufe 1 stehen. Sonst wäre die Zusage „nach drei Stunden" nicht mehr wahr, sobald jemand einen Kollegen hinzufügt.',
          '„Prio" ist die Reihenfolge innerhalb einer Stufe. Gleiche Zahl bei mehreren Personen heißt: gleichzeitig ansprechen.',
          '„wartet … Min." ist die Zeit, die auf diese Person gewartet wird, bevor die nächste Priorität derselben Stufe drankommt.',
          'Wird ein PC-Benutzer ausgewählt, kommen Mail und Slack-Kennung aus seinem Konto und lassen sich hier nicht überschreiben. Zwei Wahrheiten über dieselbe Person wären genau die Sorte Fehler, die erst auffällt, wenn es darauf ankommt.',
          '„Person ohne PC-Konto" ist für jemanden, der erreichbar sein soll, aber keinen Zugang hat — etwa die Geschäftsführung. Dann werden Name, Mail und Slack-ID hier eingetragen.',
        ],
      },
      {
        titel: 'Plan: Zeitregeln',
        punkte: [
          'Es gibt zwei Wirkungen, und der Unterschied ist wichtig. „Fristen ×" streckt die Abstände — die Kette läuft weiter, nur langsamer. „erst ab … Uhr" schiebt sie auf: bis dahin wird niemand angesprochen, die Kette beginnt erst zur genannten Stunde.',
          'Für einen Betrieb ohne Nachtbereitschaft ist Aufschieben das richtige Werkzeug: die Vorlage „Nachts nicht wecken" lässt Meldungen zwischen 20 und 8 Uhr liegen und beginnt morgens um acht. Der Alarm bleibt die ganze Zeit sichtbar — er weckt nur niemanden.',
          '„ganz aussetzen" ist etwas anderes als Aufschieben: es fällt unter den Tisch. Mit Bedacht — was hier stumm bleibt, merkt niemand.',
          'Bei Feiertagen lässt sich auswählen, welche konkret gemeint sind. Ohne Auswahl gelten alle gesetzlichen des Bundeslands. Meist sind nur einige gemeint: zwischen den Jahren sitzt niemand am Rechner, an Fronleichnam schon.',
          'Feiertage werden gerechnet, nicht gepflegt — auch die beweglichen (Ostern, Christi Himmelfahrt, Pfingsten). Heiligabend und Silvester stehen mit zur Auswahl: gesetzlich keine Feiertage, praktisch erreicht man an ihnen niemanden.',
          'Treffen mehrere Regeln zu (Sonntag UND Feiertag), gilt die mildeste. Aufschieben gewinnt gegen Fristen strecken — wo niemand da ist, nützt eine gestreckte Frist nichts.',
        ],
      },
      {
        titel: 'Prüfen: Vorschau, Probemeldung, Übung',
        punkte: [
          '„Vorschau" rechnet den Ablauf durch, ohne irgendjemanden zu benachrichtigen: wer wäre wann dran, wenn jetzt ein Alarm käme. Berücksichtigt die geltenden Zeitregeln — wer sie sonntags öffnet, sieht die gestreckten Fristen.',
          '„Probemeldung" stellt wirklich zu und beantwortet die andere Frage: kommt es an? Jede Person im Plan bekommt eine klar gekennzeichnete Testmeldung, und die Auswertung zeigt je Kanal, ob es geklappt hat. Genau daran scheitert es im Ernstfall: eine Adresse mit Tippfehler, eine fehlende Slack-Kennung.',
          '„Übung starten" lässt die echte Kette im Zeitraffer durchlaufen — 60-fach, aus drei Stunden werden drei Minuten. Damit lässt sich sehen, ob die Eskalation wirklich greift: springt sie weiter, wenn niemand übernimmt, und stoppt sie, wenn doch. Es entsteht ein Alarm namens PROBELAUF, der nach 15 Minuten von selbst endet.',
          'Die Übung benachrichtigt wirklich. Sie ist als Übung gekennzeichnet, aber die Kollegen bekommen echte Nachrichten — besser vorher Bescheid sagen.',
        ],
      },
      {
        titel: 'Voraussetzungen',
        punkte: [
          'Slack: eine Incoming-Webhook-Adresse in den Einstellungen unter „Alarme". Der Kanal steckt in der Adresse.',
          'Für Erwähnungen mit Signalton braucht jede Person ihre Slack-Mitglieds-ID (U01ABC…) am Benutzerkonto. Ein @name im Text sieht aus wie eine Erwähnung, benachrichtigt aber niemanden.',
          'Mit einem Slack-Bot-Token kann der PC die Kennungen selbst nachschlagen — über „Abfragen" beim Benutzer oder „Slack-IDs holen" für alle. Findet er die Mailadresse nicht, sucht er über den Namen und schlägt Treffer vor.',
          'Fehlt eine Slack-Kennung, kommen Mail und Benachrichtigung im PC trotzdem an. Nur die Erwähnung bleibt aus.',
        ],
      },
    ],
  },
  '/users': {
    title: 'Nutzer',
    intro:
      'Konten für juni.com-Mitarbeitende. Kunden aus dem Customer Center sind hier bewusst nicht enthalten — das sind getrennte Systeme ohne gemeinsamen Login.',
    points: [
      'Nur Administratoren können Konten anlegen, Rollen ändern oder sperren. Eine Selbstregistrierung gibt es nicht.',
      'Beim Anlegen wird kein Passwort vergeben: der Nutzer bekommt eine Einladung mit einem Link, über den er sich selbst eins setzt (24 Stunden gültig).',
      'Rollen steuern die Schreibrechte. Lesen darf jeder angemeldete Mitarbeitende alles — Administrator (alles), Operations (Server/Instanzen/Deployments), Produktmanagement (Produkte/Tarife/Releases), Vertrieb (Kunden/Bestellungen), Buchhaltung (Kundenstammdaten), Technischer Support (nur lesen).',
      'Gesperrte Konten können sich sofort nicht mehr anmelden; offene Einladungs- und Reset-Links verfallen dabei.',
      'Sie können weder Ihre eigene Rolle entziehen noch Ihr eigenes Konto sperren, und der letzte aktive Administrator lässt sich nicht herabstufen — sonst wäre niemand mehr handlungsfähig.',
      'Über "Aktivität" sehen Sie, was ein Nutzer konkret getan hat; die vollständige Historie steht im Audit-Log.',
    ],
  },
};

export function resolveHelp(pathname: string): HelpContent | null {
  if (HELP_CONTENT[pathname]) return HELP_CONTENT[pathname];
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length >= 2) {
    const base = `/${parts[0]}`;
    if (HELP_CONTENT[base]) return HELP_CONTENT[base];
  }
  return null;
}
